import React, { useCallback, useEffect, useRef, useState } from 'react';
import { attachWindowPointerDrag } from '../../utils/windowPointerDrag';
import type { SegmentRange } from './types';
import { COMPARISON_COLORS } from './types';

function secToHMS(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

interface SegmentTimelineBarProps {
  label: string;
  color: string;
  durationSec: number;
  segment: SegmentRange | null;
  /** Live preview while dragging — chart only, no backend. */
  onPreviewChange?: (seg: SegmentRange | null) => void;
  /** Called when the user releases a handle — updates draft segment only. */
  onDraftChange: (seg: SegmentRange) => void;
  disabled?: boolean;
}

export function SegmentTimelineBar({
  label,
  color,
  durationSec,
  segment,
  onPreviewChange,
  onDraftChange,
  disabled,
}: SegmentTimelineBarProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const onPreviewRef = useRef(onPreviewChange);
  onPreviewRef.current = onPreviewChange;

  const [dragSegment, setDragSegment] = useState<SegmentRange | null>(null);
  const dur = Math.max(durationSec, 1);

  const committedStart = segment?.startSec ?? 0;
  const committedEnd = segment?.endSec ?? durationSec;
  const start = dragSegment?.startSec ?? committedStart;
  const end = dragSegment?.endSec ?? committedEnd;

  useEffect(() => {
    setDragSegment(null);
    onPreviewRef.current?.(null);
  }, [committedStart, committedEnd]);

  const pct = (v: number) => `${(v / dur) * 100}%`;

  const computeSegment = useCallback(
    (which: 'start' | 'end', clientX: number, base: SegmentRange): SegmentRange => {
      const track = trackRef.current;
      if (!track) return base;
      const rect = track.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const t = Math.round(frac * dur);
      if (which === 'start') {
        return { startSec: Math.min(t, base.endSec - 5), endSec: base.endSec };
      }
      return { startSec: base.startSec, endSec: Math.max(t, base.startSec + 5) };
    },
    [dur],
  );

  const beginDrag = useCallback(
    (which: 'start' | 'end') => (e: React.PointerEvent) => {
      if (disabled) return;
      const base: SegmentRange = { startSec: committedStart, endSec: committedEnd };

      attachWindowPointerDrag(
        e,
        (clientX) => {
          const next = computeSegment(which, clientX, base);
          setDragSegment(next);
          onPreviewRef.current?.(next);
        },
        (clientX) => {
          const finalSeg = computeSegment(which, clientX, base);
          setDragSegment(null);
          onPreviewRef.current?.(null);
          onDraftChange(finalSeg);
        },
      );
    },
    [committedEnd, committedStart, computeSegment, disabled, onDraftChange],
  );

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11, fontWeight: 600, color: 'var(--fg-2)' }}>
        <span style={{ color }}>{label}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--fg-3)' }}>
          {secToHMS(start)} – {secToHMS(end)}
        </span>
      </div>
      <div
        ref={trackRef}
        data-no-pull
        style={{
          position: 'relative',
          height: 28,
          borderRadius: 8,
          background: 'var(--surface-sunken)',
          boxShadow: 'var(--nm-inset-sm)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: pct(start),
            width: pct(end - start),
            top: 4,
            bottom: 4,
            borderRadius: 6,
            background: color,
            opacity: 0.35,
          }}
        />
        {(['start', 'end'] as const).map(which => (
          <div
            key={which}
            role="slider"
            tabIndex={0}
            onPointerDown={disabled ? undefined : beginDrag(which)}
            className="chart-touch-handle"
            style={{
              position: 'absolute',
              left: which === 'start' ? pct(start) : pct(end),
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 22,
              height: 28,
              borderRadius: 4,
              background: color,
              touchAction: 'none',
              boxShadow: 'var(--nm-raised-sm)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

export function defaultSegment(durationSec: number): SegmentRange {
  return { startSec: 0, endSec: Math.max(durationSec, 0) };
}

/** Colors for segment bars */
export const SEGMENT_COLORS = { a: COMPARISON_COLORS.a, b: COMPARISON_COLORS.b };
